/**
 * Interactive Calculators for Academy
 * Revenue, Break-Even, Rate Negotiation calculators
 */

import { useState } from 'react';
import { 
  Calculator, DollarSign, Target, TrendingUp, Truck,
  Fuel, Wrench, Shield, Clock, MapPin, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Info, RefreshCw
} from 'lucide-react';

// Revenue/Profit Calculator
export const RevenueCalculator = ({ courseType = 'trucking' }) => {
  const [inputs, setInputs] = useState({
    loadsPerWeek: 3,
    avgMiles: 800,
    ratePerMile: 2.50,
    fuelCostPerGallon: 3.80,
    mpg: 6.5,
    insuranceMonthly: 1500,
    truckPayment: 2000,
    maintenanceMonthly: 500,
    otherExpenses: 800,
    dispatchFee: 5, // percentage
  });
  
  const [showDetails, setShowDetails] = useState(false);
  
  // Calculations
  const weeklyMiles = inputs.loadsPerWeek * inputs.avgMiles;
  const monthlyMiles = weeklyMiles * 4;
  
  const grossRevenue = monthlyMiles * inputs.ratePerMile;
  const fuelCost = (monthlyMiles / inputs.mpg) * inputs.fuelCostPerGallon;
  const dispatchCost = grossRevenue * (inputs.dispatchFee / 100);
  const totalExpenses = fuelCost + inputs.insuranceMonthly + inputs.truckPayment + 
                       inputs.maintenanceMonthly + inputs.otherExpenses + dispatchCost;
  const netProfit = grossRevenue - totalExpenses;
  const profitMargin = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0;
  const effectiveRPM = monthlyMiles > 0 ? netProfit / monthlyMiles : 0;
  
  const updateInput = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };
  
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6" data-testid="revenue-calculator">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-green-400" />
          <h3 className="text-white font-bold text-lg">Revenue & Profit Calculator</h3>
        </div>
        <button 
          onClick={() => setInputs({
            loadsPerWeek: 3, avgMiles: 800, ratePerMile: 2.50, fuelCostPerGallon: 3.80,
            mpg: 6.5, insuranceMonthly: 1500, truckPayment: 2000, maintenanceMonthly: 500,
            otherExpenses: 800, dispatchFee: 5
          })}
          className="text-white/40 hover:text-white text-sm flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Reset
        </button>
      </div>
      
      {/* Input Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <InputField label="Loads/Week" value={inputs.loadsPerWeek} onChange={v => updateInput('loadsPerWeek', v)} icon={Truck} />
        <InputField label="Avg Miles/Load" value={inputs.avgMiles} onChange={v => updateInput('avgMiles', v)} icon={MapPin} />
        <InputField label="Rate/Mile ($)" value={inputs.ratePerMile} onChange={v => updateInput('ratePerMile', v)} icon={DollarSign} step="0.10" />
        <InputField label="Fuel $/Gallon" value={inputs.fuelCostPerGallon} onChange={v => updateInput('fuelCostPerGallon', v)} icon={Fuel} step="0.10" />
        <InputField label="MPG" value={inputs.mpg} onChange={v => updateInput('mpg', v)} icon={Fuel} step="0.5" />
        <InputField label="Dispatch Fee (%)" value={inputs.dispatchFee} onChange={v => updateInput('dispatchFee', v)} icon={Clock} />
      </div>
      
      {/* Expandable Expenses */}
      <button 
        onClick={() => setShowDetails(!showDetails)}
        className="w-full flex items-center justify-between p-3 bg-white/5 border border-white/10 mb-4"
      >
        <span className="text-white/70 text-sm">Monthly Fixed Expenses</span>
        <div className="flex items-center gap-2">
          <span className="text-white font-medium">${(inputs.insuranceMonthly + inputs.truckPayment + inputs.maintenanceMonthly + inputs.otherExpenses).toLocaleString()}</span>
          {showDetails ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
        </div>
      </button>
      
      {showDetails && (
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white/5 border border-white/10">
          <InputField label="Insurance/Month" value={inputs.insuranceMonthly} onChange={v => updateInput('insuranceMonthly', v)} icon={Shield} />
          <InputField label="Truck Payment" value={inputs.truckPayment} onChange={v => updateInput('truckPayment', v)} icon={Truck} />
          <InputField label="Maintenance" value={inputs.maintenanceMonthly} onChange={v => updateInput('maintenanceMonthly', v)} icon={Wrench} />
          <InputField label="Other Expenses" value={inputs.otherExpenses} onChange={v => updateInput('otherExpenses', v)} icon={DollarSign} />
        </div>
      )}
      
      {/* Results */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <ResultCard label="Monthly Gross" value={grossRevenue} prefix="$" color="white" />
          <ResultCard label="Monthly Expenses" value={totalExpenses} prefix="$" color="red" />
        </div>
        
        <div className={`p-4 border ${netProfit >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
          <div className="flex items-center justify-between">
            <span className="text-white font-bold">Monthly Net Profit</span>
            <span className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${netProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="flex items-center justify-between mt-2 text-sm">
            <span className="text-white/50">Profit Margin</span>
            <span className={profitMargin >= 20 ? 'text-green-400' : profitMargin >= 10 ? 'text-yellow-400' : 'text-red-400'}>
              {profitMargin.toFixed(1)}%
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/50">Effective $/Mile (Net)</span>
            <span className="text-white">${effectiveRPM.toFixed(2)}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/5 p-3">
            <div className="text-white/50 text-xs">Weekly</div>
            <div className="text-white font-bold">${(netProfit / 4).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          </div>
          <div className="bg-white/5 p-3">
            <div className="text-white/50 text-xs">Yearly</div>
            <div className="text-white font-bold">${(netProfit * 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          </div>
          <div className="bg-white/5 p-3">
            <div className="text-white/50 text-xs">Miles/Month</div>
            <div className="text-white font-bold">{monthlyMiles.toLocaleString()}</div>
          </div>
        </div>
      </div>
      
      {/* Pro Tips */}
      {profitMargin < 15 && (
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="text-yellow-400 font-medium">Low Margin Alert:</span>
            <span className="text-white/70"> Try increasing your rate per mile or reducing deadhead to improve profitability.</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Break-Even Calculator
export const BreakEvenCalculator = ({ courseType = 'trucking' }) => {
  const [inputs, setInputs] = useState({
    monthlyFixedCosts: 4800,
    avgRatePerLoad: 2000,
    avgCostPerLoad: 800,
    avgMilesPerLoad: 800,
    targetProfit: 5000,
  });
  
  const profitPerLoad = inputs.avgRatePerLoad - inputs.avgCostPerLoad;
  const breakEvenLoads = profitPerLoad > 0 ? Math.ceil(inputs.monthlyFixedCosts / profitPerLoad) : 0;
  const loadsForTarget = profitPerLoad > 0 ? Math.ceil((inputs.monthlyFixedCosts + inputs.targetProfit) / profitPerLoad) : 0;
  const breakEvenMiles = breakEvenLoads * inputs.avgMilesPerLoad;
  
  const updateInput = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };
  
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6" data-testid="breakeven-calculator">
      <div className="flex items-center gap-3 mb-6">
        <Target className="w-6 h-6 text-purple-400" />
        <h3 className="text-white font-bold text-lg">Break-Even Calculator</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <InputField label="Monthly Fixed Costs" value={inputs.monthlyFixedCosts} onChange={v => updateInput('monthlyFixedCosts', v)} icon={DollarSign} />
        <InputField label="Avg Rate/Load" value={inputs.avgRatePerLoad} onChange={v => updateInput('avgRatePerLoad', v)} icon={DollarSign} />
        <InputField label="Avg Cost/Load (Fuel, etc)" value={inputs.avgCostPerLoad} onChange={v => updateInput('avgCostPerLoad', v)} icon={Fuel} />
        <InputField label="Target Monthly Profit" value={inputs.targetProfit} onChange={v => updateInput('targetProfit', v)} icon={Target} />
      </div>
      
      {/* Results */}
      <div className="space-y-4">
        <div className="bg-purple-500/10 border border-purple-500/30 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/70">Profit Per Load</span>
            <span className="text-purple-400 font-bold text-lg">${profitPerLoad.toLocaleString()}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#121212] border border-white/10 p-4 text-center">
            <div className="text-white/50 text-sm mb-1">Break-Even Point</div>
            <div className="text-3xl font-bold text-white">{breakEvenLoads}</div>
            <div className="text-white/40 text-sm">loads/month</div>
            <div className="text-white/30 text-xs mt-1">{breakEvenMiles.toLocaleString()} miles</div>
          </div>
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 text-center">
            <div className="text-white/50 text-sm mb-1">To Hit ${inputs.targetProfit.toLocaleString()} Profit</div>
            <div className="text-3xl font-bold text-[#D4AF37]">{loadsForTarget}</div>
            <div className="text-white/40 text-sm">loads/month</div>
            <div className="text-white/30 text-xs mt-1">{(loadsForTarget * inputs.avgMilesPerLoad).toLocaleString()} miles</div>
          </div>
        </div>
        
        {/* Visual Progress */}
        <div className="bg-white/5 p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-white/50">Load Progress to Break-Even</span>
            <span className="text-white">{breakEvenLoads} loads = $0 profit</span>
          </div>
          <div className="h-3 bg-white/10 overflow-hidden">
            {[...Array(Math.min(breakEvenLoads, 10))].map((_, i) => (
              <div 
                key={i}
                className="inline-block h-full bg-purple-500"
                style={{ width: `${100 / Math.min(breakEvenLoads, 10)}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-white/30 mt-1">
            <span>0</span>
            <span>Break-Even ({breakEvenLoads})</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Rate Negotiation Simulator
export const RateNegotiationSimulator = () => {
  const [scenario, setScenario] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [scenarioIndex, setScenarioIndex] = useState(0);
  
  const scenarios = [
    {
      id: 1,
      broker: "I have a load from Dallas to Atlanta, 780 miles. I can pay $1,400.",
      context: "Market rate: $2.00-$2.20/mile. Your operating cost: $1.50/mile.",
      responses: [
        { text: "That works for me, I'll take it.", result: "loss", feedback: "At $1.79/mile, you're barely covering costs. Always know your minimum.", points: 0 },
        { text: "I need at least $1,800 for that lane.", result: "good", feedback: "Good counter! You're at $2.30/mile which covers costs and leaves profit.", points: 80 },
        { text: "My rate for Dallas-Atlanta is $1,950. I can do $1,850 if you have return freight.", result: "excellent", feedback: "Excellent! You anchored high and offered value. This shows professionalism.", points: 100 },
        { text: "No way, that's insulting. Call me when you're serious.", result: "poor", feedback: "Never burn bridges. A firm but professional counter keeps doors open.", points: 20 }
      ]
    },
    {
      id: 2,
      broker: "I need this picked up TODAY. It's 500 miles for $900. Can you do it?",
      context: "Last-minute loads often pay premium. Your operating cost: $1.50/mile.",
      responses: [
        { text: "I can do $900 if pickup is within 2 hours.", result: "loss", feedback: "At $1.80/mile for a rush load? You left money on the table.", points: 30 },
        { text: "For same-day pickup, I need $1,200 minimum.", result: "good", feedback: "Good! You recognized the urgency premium. That's $2.40/mile.", points: 85 },
        { text: "Same-day is $1,300, but I can do $1,150 if you can guarantee next-day delivery window.", result: "excellent", feedback: "Perfect! You valued your flexibility and created win-win options.", points: 100 },
        { text: "Let me check my schedule and call you back.", result: "poor", feedback: "In hot freight, hesitation loses loads. Know your rates and decide fast.", points: 10 }
      ]
    },
    {
      id: 3,
      broker: "I've got consistent weekly freight, Chicago to Nashville. 470 miles. What's your rate?",
      context: "Consistent freight is valuable. Market: $2.10/mile. Your cost: $1.50/mile.",
      responses: [
        { text: "$950 per load.", result: "loss", feedback: "At $2.02/mile, you're leaving consistent money behind.", points: 40 },
        { text: "$1,100 per load for weekly commitment.", result: "good", feedback: "Solid at $2.34/mile. Good balance of rate and relationship building.", points: 80 },
        { text: "$1,100 standard, or $1,000/load with a 3-month contract guarantee.", result: "excellent", feedback: "Brilliant! You offered volume discount while securing commitment.", points: 100 },
        { text: "What's your budget?", result: "poor", feedback: "Never ask their budget first. It anchors low. State your value.", points: 25 }
      ]
    }
  ];
  
  const startSimulation = () => {
    setScenario(scenarios[scenarioIndex]);
    setSelectedResponse(null);
    setShowResult(false);
  };
  
  const selectResponse = (response) => {
    setSelectedResponse(response);
    setShowResult(true);
    setScore(prev => prev + response.points);
  };
  
  const nextScenario = () => {
    if (scenarioIndex < scenarios.length - 1) {
      setScenarioIndex(prev => prev + 1);
      setScenario(scenarios[scenarioIndex + 1]);
      setSelectedResponse(null);
      setShowResult(false);
    } else {
      setScenario(null);
      setScenarioIndex(0);
    }
  };
  
  const resetSimulation = () => {
    setScenario(null);
    setSelectedResponse(null);
    setShowResult(false);
    setScore(0);
    setScenarioIndex(0);
  };
  
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4 md:p-6" data-testid="negotiation-simulator">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-6 h-6 text-[#D4AF37]" />
          <h3 className="text-white font-bold text-lg">Rate Negotiation Simulator</h3>
        </div>
        {scenario && (
          <div className="text-sm">
            <span className="text-white/50">Score:</span>
            <span className="text-[#D4AF37] font-bold ml-2">{score}</span>
          </div>
        )}
      </div>
      
      {!scenario ? (
        <div className="text-center py-8">
          <TrendingUp className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-4" />
          <h4 className="text-white font-bold text-xl mb-2">Practice Your Negotiation Skills</h4>
          <p className="text-white/60 mb-6 max-w-md mx-auto">
            Work through real-world scenarios to learn how to negotiate better rates with brokers.
          </p>
          <button
            onClick={startSimulation}
            className="px-6 py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] transition-colors"
          >
            Start Simulation
          </button>
          {score > 0 && (
            <div className="mt-4 text-white/50">
              Previous Score: <span className="text-[#D4AF37]">{score}/{scenarios.length * 100}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Progress */}
          <div className="flex items-center gap-2 text-sm text-white/50">
            <span>Scenario {scenarioIndex + 1} of {scenarios.length}</span>
            <div className="flex-1 h-1 bg-white/10 rounded">
              <div 
                className="h-full bg-[#D4AF37] rounded transition-all"
                style={{ width: `${((scenarioIndex + 1) / scenarios.length) * 100}%` }}
              />
            </div>
          </div>
          
          {/* Broker Message */}
          <div className="bg-[#121212] border border-white/10 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-400 font-bold">B</span>
              </div>
              <div>
                <div className="text-white/50 text-xs mb-1">Broker says:</div>
                <p className="text-white">{scenario.broker}</p>
              </div>
            </div>
          </div>
          
          {/* Context */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-white/70">{scenario.context}</p>
          </div>
          
          {/* Response Options */}
          {!showResult ? (
            <div className="space-y-2">
              <p className="text-white/50 text-sm">How do you respond?</p>
              {scenario.responses.map((response, idx) => (
                <button
                  key={idx}
                  onClick={() => selectResponse(response)}
                  className="w-full text-left p-4 bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all"
                >
                  <span className="text-white">{response.text}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selected Response */}
              <div className={`p-4 border ${
                selectedResponse.result === 'excellent' ? 'bg-green-500/10 border-green-500/30' :
                selectedResponse.result === 'good' ? 'bg-blue-500/10 border-blue-500/30' :
                selectedResponse.result === 'poor' ? 'bg-yellow-500/10 border-yellow-500/30' :
                'bg-red-500/10 border-red-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {selectedResponse.result === 'excellent' && <CheckCircle className="w-5 h-5 text-green-400" />}
                  {selectedResponse.result === 'good' && <CheckCircle className="w-5 h-5 text-blue-400" />}
                  {selectedResponse.result === 'poor' && <AlertCircle className="w-5 h-5 text-yellow-400" />}
                  {selectedResponse.result === 'loss' && <AlertCircle className="w-5 h-5 text-red-400" />}
                  <span className={`font-bold uppercase text-sm ${
                    selectedResponse.result === 'excellent' ? 'text-green-400' :
                    selectedResponse.result === 'good' ? 'text-blue-400' :
                    selectedResponse.result === 'poor' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {selectedResponse.result === 'excellent' ? 'Excellent!' :
                     selectedResponse.result === 'good' ? 'Good Move' :
                     selectedResponse.result === 'poor' ? 'Could Be Better' :
                     'Money Left on Table'}
                  </span>
                  <span className="ml-auto text-white/50">+{selectedResponse.points} pts</span>
                </div>
                <p className="text-white/80 text-sm">{selectedResponse.feedback}</p>
              </div>
              
              {/* Next Button */}
              <button
                onClick={nextScenario}
                className="w-full py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] transition-colors"
              >
                {scenarioIndex < scenarios.length - 1 ? 'Next Scenario' : 'See Final Score'}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Final Score (when all scenarios complete) */}
      {!scenario && score > 0 && scenarioIndex === 0 && (
        <div className="mt-6 p-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-center">
          <h4 className="text-[#D4AF37] font-bold text-xl mb-2">Simulation Complete!</h4>
          <div className="text-4xl font-bold text-white mb-2">{score}/{scenarios.length * 100}</div>
          <p className="text-white/60 mb-4">
            {score >= 250 ? "Expert Negotiator! You know how to maximize value." :
             score >= 180 ? "Good skills! A bit more practice and you'll be a pro." :
             score >= 100 ? "Getting there! Review the feedback and try again." :
             "Keep practicing! Negotiation is a skill that improves with experience."}
          </p>
          <button
            onClick={resetSimulation}
            className="px-6 py-2 bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

// Input Field Component
const InputField = ({ label, value, onChange, icon: Icon, step = "1" }) => (
  <div>
    <label className="text-white/50 text-xs mb-1 block">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        step={step}
        className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 pl-10 focus:border-[#D4AF37]/50 focus:outline-none"
      />
    </div>
  </div>
);

// Result Card Component
const ResultCard = ({ label, value, prefix = "", suffix = "", color = "white" }) => (
  <div className="bg-[#121212] border border-white/10 p-4 text-center">
    <div className="text-white/50 text-sm mb-1">{label}</div>
    <div className={`text-xl font-bold ${color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : 'text-white'}`}>
      {prefix}{typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : value}{suffix}
    </div>
  </div>
);

export default {
  RevenueCalculator,
  BreakEvenCalculator,
  RateNegotiationSimulator
};
